import { isValidDate } from "@components/inputs/date/utils";
import { BackgroundJobEntity, EntityTypeName } from "@odata/GeneratedEntityTypes";
import { BackgroundJobTypeCode } from "@odata/GeneratedEnums";
import { BackgroundJobParameters as Params, IBackgroundJobCustomData } from "@pages/backgroundJob/BackgroundJob.utils";
import {
    StyledBackgroundJobParameters
} from "@pages/backgroundJob/BackgrounJob.styles";
import { getDocumentTypeName } from "@pages/documents/Document.utils";
import { isNotDefined } from "@utils/general";
import i18next from "i18next";
import React from "react";

import { TRecordAny } from "../../global.types";
import DateType from "../../types/Date";
import { FormStorage } from "../../views/formView/FormStorage";


interface IProps {
    storage: FormStorage;
}

export class BackgroundJobParameters extends React.Component<IProps> {
    get Parameters(): Params {
        return (this.props.storage as FormStorage<{}, IBackgroundJobCustomData>).getCustomData()?.parameters || {};
    }

    get Type() {
        return this.props.storage.data.entity[BackgroundJobEntity.Type]?.Code;
    }

    getLabelNames = (value: TRecordAny[]): string => {
        return value.map(val => val.Name).join(", ");
    }

    getAssignmentName = (value: TRecordAny) => {
        if (value?.CreditAccountNumber && value?.DebitAccountNumber) {
            return `${value?.DebitAccountNumber}/${value.CreditAccountNumber} ${value.AccountAssignmentName}`;
        }
        return "";
    }

    parseValue = (value: TRecordAny) => {
        if (isNotDefined(value)) {
            return i18next.t(`BackgroundJobs:Form.NoChange`);
        }

        if (isValidDate(value as Date)) {
            return DateType.format(value as Date);
        }

        if (Array.isArray(value)) {
            return this.getLabelNames(value);
        }

        if (typeof value === "object") {
            if (value.Date) {
                return DateType.format(value.Date);
            }

            if (value.Name) {
                return value.Name;
            }

            if (value.AccountAssignment) {
                return this.getAssignmentName(value.AccountAssignment);
            }


            if (value.Source) {
                return i18next.t(`BackgroundJobs:BulEditSource.${value.Source}`);
            }
        } else {
            return value;
        }

        return "";
    };

    parseChanges = () => {
        return (this.Parameters.Changes || []).map((change: TRecordAny) => {
            const field = change.ChangeType;
            const translation = i18next.t(`BackgroundJobs:Fields.${field}`);
            const type = change.Kind;
            const typeTranslation = i18next.t(`BackgroundJobs:BulkEdit.${type}`);
            const value = this.parseValue(change[field] ?? change.Value);

            return (
                <>
                    {translation} - {typeTranslation}: {value}
                </>
            );
        });
    }

    createRenderedParams = (items: TRecordAny[]) => {
        return items?.map(item => {
                const translation = i18next.t(`BackgroundJobs:Fields.${item.field}`);
                const value = item.value ?? this.parseValue((this.Parameters as TRecordAny)[item.field]);

                return (
                    <><b>{translation}: </b> {value}</>
                );
            }
        );
    }

    parseMultiplePostDrafts = () => {
        const param = this.Parameters as TRecordAny;
        const accAss =
            param.CreditAccountNumber && param.DebitAccountNumber ? `${param.CreditAccountNumber}/${param.DebitAccountNumber} ${param.AccountAssignmentName}` : null;

        const items = [{
            field: "AccountAssignmentSelection",
            value: accAss
        }, {
            field: "DateAccountingTransaction",
        }];

        return this.createRenderedParams(items);
    }

    parseParametricParams = () => {
        if (this.Type === BackgroundJobTypeCode.PostMultipleDrafts) {
            return this.parseMultiplePostDrafts();
        }

        if (this.Parameters.Changes) {
            return this.parseChanges();
        }

        return [];
    }


    render() {
        if (!this.Parameters) {
            return null;
        }

        const count = this.Parameters.DocumentIds?.length ?? this.Parameters.DraftIds?.length;
        const type = this.Parameters.DocumentTypeCode as EntityTypeName;
        const params = this.parseParametricParams();

        const items = [
            <>
                <b>{i18next.t("BackgroundJobs:Fields.DocumentType")}</b>:{" "}
                {getDocumentTypeName(type) as string}
            </>,
            <>
                <b>{i18next.t("BackgroundJobs:Fields.DocumentCount")}</b>:{" "}
                {count}
            </>,
        ];

        return (
            <StyledBackgroundJobParameters>
                {items.map((item, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && (
                            <span> | </span>
                        )}
                        {item}
                    </React.Fragment>
                ))}

                {params.length > 0 && (
                    <>
                        <span> | </span>
                        <b>{i18next.t("BackgroundJobs:BulkEdit.Action")}</b>:{" "}
                        {params.map((param, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <span>,</span>}{" "}
                                {param}
                            </React.Fragment>
                        ))}
                    </>
                )}
            </StyledBackgroundJobParameters>
        );
    }
}